import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reject, map, findIndex, orderBy } from 'lodash';
import Fa from 'react-fontawesome';
import { Link, withRouter } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ProgressBar from '../../../../components/progress-bar';
import ConfirmButton from '../../../../components/confirm-button';
import PathDisplay from '../../../../components/search-path/path';
import AddLanguageForm from './AddLanguageForm';

import {
  fetchAllFeedbackLanguageSettings,
  orderFeedbackLanguageSettings,
  deleteFeedbackLanguageSettings,
} from '../../SetupActions';


import styles from './styles.css';

/** A little function to help us with reordering the result */
const mutatedReorder = (list, startIndex, endIndex) => {
  const [removed] = list.splice(startIndex, 1);
  list.splice(endIndex, 0, removed);
  return list;
};

class FeedbackLanguageIndexPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loaded: false,
      settings: [],
      deleting: {}, // indexed by id
      updating: {}, // indexed by id
      dragging: false,
      showModal: false,
    };
    this.deleteSettings = this.deleteSettings.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
  }
  
  componentDidMount() {
    this.fetchData();
  }

  showModal() {
    this.setState({ showModal: true });
  }

  closeModal() {
    this.setState({ showModal: false });
  }

  fetchData() {
    this.setState({ loading: true, loaded: false }, () => {
      this.props.dispatch(fetchAllFeedbackLanguageSettings(this.props.match.params.source))
        .then(
          (settings) => {
            this.setState({ settings, loaded: true, loading: false });
          },
          () => {

          },
        );
    });
  }

  onDragEnd(result) {
    const { source, destination } = result;
    if (!this.state.dragging && source.index !== destination.index) {
      const destinationList = [...this.state.settings];
      const draggedId = this.state.settings[source.index] ? this.state.settings[source.index]._id : null;
      mutatedReorder(
        destinationList,
        source.index,
        destination.index,
      );
      const positions = map(destinationList, '_id');
      this.setState({ dragging: true, draggedId: null }, () => {
        this.props.dispatch(orderFeedbackLanguageSettings(this.props.match.params.source, { ids: positions }))
          .then(
            (settings) => {
              this.setState({ settings, dragging: false, draggedId });
            },
            () => {
              this.setState({ dragging: false, draggedId });
            },
          );
      });
    }
  }

  deleteSettings(setting) {
    const { deleting } = this.state;

    if (deleting[setting._id] === true) {
      return;
    }
    this.setState({ deleting: { ...deleting, [setting._id]: true } }, () => {
      this.props.dispatch(deleteFeedbackLanguageSettings(setting._id))
        .then(
          () => {
            const newSettings = reject(this.state.settings, (s) => s._id === setting._id);
            this.setState({ settings: newSettings, deleting: { ...deleting, [setting._id]: false } });
          },
          () => {
            this.setState({ deleting: { ...deleting, [setting._id]: false } });
          },
        );
    });
  }

  renderActions(setting) {
    const { deleting, updating } = this.state;

    const isDeleting = deleting[setting._id] === true;
    const isUpdating = updating[setting._id] === true;

    const isDisabled = isDeleting || isUpdating;
    return (
      <ConfirmButton
        size="xs"
        disabled={isDisabled}
        onConfirm={() => { this.deleteSettings(setting); }}
      >
        <Fa name={isDeleting ? 'spinner' : 'trash'} spin={isDeleting} /> Delete
      </ConfirmButton>
    )
  }

  handleSuccess(setting) {
    const settings = [...this.state.settings];
    const idx = findIndex(settings, { _id: setting._id });
    if (idx >= 0) {
      settings[idx] = setting;
    } else {
      settings.push(setting);
    }
    this.setState({ settings: orderBy(settings, 'order', 'asc') })
  }

  renderSearchPath(setting) {
    if (!setting || !setting.search_paths) {
      return null;
    }

    const isAccount = this.props.match.params.source === 'accounts';
    const inaccessible = setting.accessible === false;

    return (
      <span>
        {inaccessible && <Fa name="exclamation-triangle" className="text-danger" />}
        <PathDisplay startingNode={isAccount ? 'Account' : 'Opportunity'} paths={setting.search_paths} skipMetaCheck={inaccessible} />
        {inaccessible && <span className="label label-danger text-uppercase">inaccessible field</span>}
      </span>
    );
  }

  renderRow(setting, index) {
    const { dragging, settings, draggedId } = this.state;
    const { location: { pathname } } = this.props;
    
    const blinking = draggedId === setting._id;
    const inaccessible = setting.accessible === false;

    let arrowName;
    if (dragging) {
      arrowName = 'spinner';
    } else if (settings.length > 1 && index === 0) {
      arrowName = 'long-arrow-down';
    } else if (settings.length > 1 && index === settings.length - 1) {
      arrowName = 'long-arrow-up';
    } else {
      arrowName = 'arrows-v';
    }

    return (
      <Draggable draggableId={setting._id} key={setting._id} index={index}>
        {(provided, snapshot) => (
          <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`${snapshot.isDragging ? styles.dragging : null}`}
            style={provided.draggableProps.style}
          >
            <td
              width="40"
              className="text-center"
              {...provided.dragHandleProps}
            >
              <Fa
                name={arrowName}
                spin={dragging}
                className={(settings.length < 1 || inaccessible) ? 'invisible' : ''}
              />
            </td>
            <td>
              <span className={blinking ? 'animated flash' : ''}>
                {this.renderSearchPath(setting)}
              </span>
            </td>
            <td>
              {!inaccessible &&
                <Link to={`${pathname}/${setting._id}`}>
                  <Fa name="edit" /> Edit Mappings
                </Link>
              }
            </td>
            <td>
              {this.renderActions(setting)}
            </td>
          </tr>
        )}
      </Draggable>
    );
  }

  renderCreateNewButton() {
    return (
      <div>
        <Button disabled={this.state.showModal} bsStyle="primary" onClick={this.showModal}>
          <Fa name="plus" /> Create New Mappings
        </Button>
        <AddLanguageForm
          show={this.state.showModal}
          onHide={this.closeModal}
          settings={{
            source: this.props.match.params.source === 'accounts' ? 'account' : 'opportunity',
            search_paths: [],
          }}
          onSuccess={this.handleSuccess}
        />
      </div>
    );
  }

  renderTable() {
    const { settings } = this.state;
    return (
      <div>
        <div className="clearfix">
          <div className="pull-right">
            {this.renderCreateNewButton()}
          </div>
        </div>
        <p />
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Fragment>
            <table className={`table table-bordered table-condensed table-striped ${styles.table}`}>
              <thead>
                <tr>
                  <th colSpan="2">Field</th>
                  <th width="150">Edit</th>
                  <th width="80">Delete</th>
                </tr>
              </thead>
              <Droppable droppableId="drop">
                {(provided, snapshot) => (
                  <tbody
                    ref={provided.innerRef}
                    className={snapshot.isDraggingOver ? styles.DraggingOver : null}
                  >
                    {settings.map((s, index) => this.renderRow(s, index))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </Fragment>
        </DragDropContext>

        <p>&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>
      </div>
    );
  }

  render() {
    const { loading, loaded, settings } = this.state;
    const isLoading = loading || !loaded;
    if (isLoading) {
      return (
        <div>
          <ProgressBar />
        </div>
      );
    }
    if (settings.length === 0) {
      return (
        <div>
          {this.renderCreateNewButton()}
        </div>
      );
    }
    return this.renderTable();
  }
}

/** Retrieve data from store as props */
function mapStateToProps(/* state */) {
  return {
  };
}

FeedbackLanguageIndexPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      source: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
};
FeedbackLanguageIndexPage.defaultProps = {
};
FeedbackLanguageIndexPage.contextTypes = {
};

export default withRouter(connect(mapStateToProps)(FeedbackLanguageIndexPage));
